import { Component, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Customer, CustomerAccount, FeatureFlags } from '@inyova/models';
import { cloneDeep } from 'lodash-es';

import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as AppRootActions from '@app/app.actions';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { BOND_LIST, MOBILE_VIEW_TRESHOLD } from '@app/app.constants';
import { CustomerInvestmentData } from '@shared/models/Strategy';
import { ThirdPartyServiceItem } from '@shared/models/ThirdPartService';
import { StrategyService } from '@shared/services/strategy.service';

@Component({
  selector: 'app-setup-strategy-review',
  templateUrl: './setup-strategy-review.component.html',
  styleUrls: ['./setup-strategy-review.component.scss'],
  standalone: false
})
export class SetupStrategyReviewComponent implements OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();

  bookConsultation: boolean;
  currentAccount: CustomerAccount;
  customer: Customer;
  flipperBookConsultation$: Observable<ThirdPartyServiceItem>;
  investmentData: CustomerInvestmentData;
  mobileView = false;
  bondList = BOND_LIST;
  scroll = 0;
  showScroll = false;
  CTALoading = false;
  actionInProgress = null;
  strategyWasChanged = false;
  isChangeStrategyDisabled$: Observable<boolean>;
  isSavingStrategyChangesEve2$: Observable<boolean>;
  strategyInfoLoaded$: Observable<boolean>;
  showEve2Callout$: Observable<boolean>;
  isEditMode: boolean;

  @Input() step: number;
  @Output() updateTab = new EventEmitter<number>();

  constructor(
    private store: Store,
    public router: Router,
    private strategyService: StrategyService
  ) {
    this.mobileView = window.innerWidth < MOBILE_VIEW_TRESHOLD;

    this.showEve2Callout$ = this.store.select(fromAppRoot.selectAccountFeatureFlag(FeatureFlags.STRATEGY_HIDDEN_EVE2_MIGRATION));

    this.store
      .pipe(select(fromAppRoot.selectCustomerAndCurrentInvestAccount), takeUntil(this.onDestroy$))
      .subscribe(({ customer, currentAccountDetail }) => {
        this.customer = customer;
        this.currentAccount = currentAccountDetail;
      });

    this.store.pipe(select(fromStrategy.selectCustomerInvestmentData), takeUntil(this.onDestroy$)).subscribe((response) => {
      this.investmentData = response;
    });
    this.store.pipe(select(fromStrategy.selectIsEditModeEve2), takeUntil(this.onDestroy$)).subscribe((isEditMode) => {
      this.isEditMode = isEditMode;
    });
    this.store.pipe(select(fromStrategy.selectStrategyWasChanged), takeUntil(this.onDestroy$)).subscribe(({ strategyWasChanged }) => {
      this.strategyWasChanged = strategyWasChanged;
    });
    this.isChangeStrategyDisabled$ = this.store.select(fromStrategy.selectChangeStrategyDisabled);
    this.isSavingStrategyChangesEve2$ = this.store.select(fromStrategy.selectIsSavingStrategyChangesEve2);
    this.strategyInfoLoaded$ = this.store.select(fromStrategy.selectStrategyInfoLoaded);
    this.flipperBookConsultation$ = this.store.select(
      fromAppRoot.selectThirdPartyServices('show_book_consultation')
    ) as Observable<ThirdPartyServiceItem>;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  changeStrategy(): void {
    void this.router.navigate(['/strategy/adjust']);
  }

  bookModal(value: boolean): void {
    this.bookConsultation = value;
  }

  discardStrategy(): void {
    this.store.dispatch(StrategyActions.discardStrategyChangesEve2());
  }

  saveStrategy(): void {
    this.store.dispatch(StrategyActions.saveStrategyChangesEve2());
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: { target: Window }): void {
    if (event.target.innerWidth >= MOBILE_VIEW_TRESHOLD) {
      this.mobileView = false;
    } else {
      this.mobileView = true;
    }
  }

  scrollTop(): void {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  getCTA(): string {
    if (this.currentAccount.step_status <= 2) {
      return 'cta_open_account';
    }

    if (this.currentAccount.customer_journey.id_verification_completed) {
      return 'cta_transfer_money';
    }

    // it's step 3 and verification isn't completed
    return 'cta_verification';
  }

  navigateTo() {
    if (this.currentAccount.step_status <= 2) {
      this.navigateToAccountOpening();
    } else {
      this.navigateToVerification();
    }
  }

  navigateToAccountOpening(): void {
    if (this.currentAccount.kind === 'kid') {
      this.updateKidAccountStep();
      return;
    }

    if (this.customer.saxo_bank_onboarding_done) {
      this.CTALoading = true;
      this.store.dispatch(StrategyActions.skipSaxoOnboarding());
    } else {
      void this.router.navigateByUrl('/next/saxo-account');
    }
  }

  updateKidAccountStep(): void {
    this.strategyService.updateKidsAccountStep().subscribe((value) => {
      const currentAccount = cloneDeep(this.currentAccount);

      currentAccount.step_status = 3;
      currentAccount.customer_journey.account_data_filled = value.data.attributes.account_data_filled;
      currentAccount.customer_journey.id_verification_completed = value.data.attributes.id_verification_completed;
      this.store.dispatch(AppRootActions.fetchCustomerAccountDetailSuccess({ customerAccountDetail: currentAccount }));

      void this.router.navigate(['next', 'fund']);
    });
  }

  navigateToVerification(): void {
    if (this.currentAccount.customer_journey.id_verification_completed) {
      void this.router.navigateByUrl('/next/fund');
      return;
    }

    void this.router.navigateByUrl('/next/saxo-account/start-verification');
  }

  @HostListener('window:scroll') watchScroll() {
    if (this.showScroll === false) {
      if (window.scrollY < this.scroll) {
        this.showScroll = true;
      } else {
        this.scroll = window.scrollY;
      }
    }

    if (window.scrollY < 300) {
      this.showScroll = false;
    }
  }
}
